import { useState, useEffect, useCallback } from 'react';
import { EmailTemplate, CreateEmailTemplateData, UpdateEmailTemplateData } from '@/types/emailTemplate';
import { EmailTemplateService } from '@/services/emailTemplateService';
import { useToast } from '@/hooks/use-toast';

export const useEmailTemplates = () => {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await EmailTemplateService.getAll();
      setTemplates(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch email templates';
      setError(errorMessage);
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const createTemplate = useCallback(async (templateData: CreateEmailTemplateData) => {
    try {
      const newTemplate = await EmailTemplateService.create(templateData);
      setTemplates(prev => [newTemplate, ...prev]);
      toast({
        title: "Success",
        description: "Email template created successfully",
      });
      return newTemplate;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create email template';
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
      throw err;
    }
  }, [toast]);

  const updateTemplate = useCallback(async (id: string, templateData: UpdateEmailTemplateData) => {
    try {
      const updatedTemplate = await EmailTemplateService.update(id, templateData);
      setTemplates(prev => 
        prev.map(template => 
          template.id === id ? updatedTemplate : template
        )
      );
      toast({
        title: "Success",
        description: "Email template updated successfully",
      });
      return updatedTemplate;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update email template';
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
      throw err;
    }
  }, [toast]);

  const deleteTemplate = useCallback(async (id: string) => {
    try {
      await EmailTemplateService.delete(id);
      setTemplates(prev => prev.filter(template => template.id !== id));
      toast({
        title: "Success",
        description: "Email template deleted successfully",
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete email template';
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
      throw err;
    }
  }, [toast]);

  const duplicateTemplate = useCallback(async (id: string, newName?: string) => {
    try {
      const duplicatedTemplate = await EmailTemplateService.duplicate(id, newName);
      setTemplates(prev => [duplicatedTemplate, ...prev]);
      toast({
        title: "Success",
        description: "Email template duplicated successfully",
      });
      return duplicatedTemplate;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to duplicate email template';
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
      throw err;
    }
  }, [toast]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  return {
    templates,
    loading,
    error,
    fetchTemplates,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    duplicateTemplate,
  };
};
